import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { UpdateInternalNetworkDto } from '@terra/shared-contracts-internal-network';
import { internalNetworkScopeApi } from '../../clients/tester-client/internal-network-scope-api';
import { useProjectStore } from '../../store/projectStore';
import { mapInternalNetworkToPortalTarget } from '../../utils/portal-target.utils';
import { TARGETS_QUERY_KEY } from '../useTargets';
import { INTERNAL_NETWORK_OWNED_PRINCIPALS_KEY } from './useInternalNetworkOverview';
import { INTERNAL_NETWORK_CROWN_JEWELS_KEY } from './useInternalNetworkScenarios';
import { INTERNAL_NETWORK_EXTERNAL_NETWORK_DETAIL_KEY } from './useSurfaceSettings';

const requireNetworkId = (networkId: string | null): string => {
  if (!networkId) {
    throw new Error('Network ID is required');
  }

  return networkId;
};

export function useUpdateInternalNetworkScope(networkId: string | null) {
  const queryClient = useQueryClient();
  const updateSelectedTarget = useProjectStore(
    (state) => state.updateSelectedTarget,
  );

  return useMutation({
    mutationFn: (body: UpdateInternalNetworkDto) =>
      internalNetworkScopeApi.updateInternalNetworkScope({
        networkId: requireNetworkId(networkId),
        body,
      }),
    onSuccess: (surface) => {
      queryClient.setQueryData(
        [INTERNAL_NETWORK_EXTERNAL_NETWORK_DETAIL_KEY, networkId],
        surface,
      );
      updateSelectedTarget(
        mapInternalNetworkToPortalTarget({
          ...surface,
          signalCategoriesInScope: surface.signalCategoriesInScope ?? [],
          smokeTestEnabled: surface.smokeTestEnabled ?? false,
        }),
      );
      void queryClient.invalidateQueries({ queryKey: [TARGETS_QUERY_KEY] });
    },
  });
}

export function useDeleteInternalNetworkCrownJewel(networkId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (crownJewelId: string) =>
      internalNetworkScopeApi.deleteInternalNetworkCrownJewel({
        networkId: requireNetworkId(networkId),
        crownJewelId,
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [INTERNAL_NETWORK_CROWN_JEWELS_KEY, networkId],
      }),
  });
}

export function useRevokeInternalNetworkOwnedPrincipal(
  networkId: string | null,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (entityId: string) =>
      internalNetworkScopeApi.revokeInternalNetworkOwnedPrincipal({
        networkId: requireNetworkId(networkId),
        entityId,
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [INTERNAL_NETWORK_OWNED_PRINCIPALS_KEY, networkId],
      }),
  });
}
